import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { fmtNumber } from "@/lib/i18n";
import { KIND_LABEL } from "@/lib/recipe";
import AgeDonut from "@/components/AgeDonut";

const pick = (obj, lang) => (obj && (obj[lang] || obj.en)) || "";
const imgSrc = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

function H3({ children }) {
  return <h3 className="text-[10px] uppercase tracking-[0.18em] text-ochre mb-2 font-semibold">{children}</h3>;
}

function Fact({ label, value }) {
  return (
    <div className="break-inside-avoid">
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
      <div className="stat-number text-base text-ink leading-snug">{value}</div>
    </div>
  );
}

function Facts({ country, lang, t }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 rounded-2xl border border-stoneLine bg-card p-4 break-inside-avoid" data-testid={`ebook-facts-${country.code}`}>
      <Fact label={t("capital")} value={pick(country.capital, lang)} />
      <Fact label={t("population")} value={fmtNumber(country.population, lang)} />
      <Fact label={t("area")} value={`${fmtNumber(country.area_km2, lang)} ${t("km2")}`} />
      <Fact label={t("currency")} value={pick(country.currency, lang)} />
      <Fact label={t("gov_type")} value={pick(country.gov_type, lang)} />
      {country.largest_city && <Fact label={t("largest_city")} value={`${pick(country.largest_city.name, lang)} · ≈${fmtNumber(country.largest_city.population, lang)}`} />}
    </div>
  );
}

function HistoryBlock({ country, lang, t }) {
  if (!country.historical_note) return null;
  return (
    <section className="break-inside-avoid">
      <H3>{t("history")}</H3>
      <div className="grid grid-cols-3 gap-3 mb-2 text-sm">
        <Fact label={t("independence")} value={pick(country.independence, lang)} />
        <Fact label={t("industrial_city")} value={pick(country.industrial_city, lang)} />
        <Fact label={t("religious_city")} value={pick(country.religious_city, lang)} />
      </div>
      <p className="text-sm leading-relaxed text-ink/85 border-s-2 border-ochre/40 ps-3">{pick(country.historical_note, lang)}</p>
    </section>
  );
}

function AgeBlock({ country, lang, t }) {
  if (!country.age_distribution) return null;
  const labels = [t("age_0_14"), t("age_15_29"), t("age_30_59"), t("age_60plus")];
  return (
    <section className="break-inside-avoid">
      <H3>{t("age_distribution")} · {country.age_distribution.year}</H3>
      <AgeDonut data={country.age_distribution} labels={labels} fmt={(v) => fmtNumber(Math.round(v * 10) / 10, lang)} size={110} />
    </section>
  );
}

function Places({ g, lang, t }) {
  return (
    <section>
      <H3>{t("guide_places")}</H3>
      <div className="grid grid-cols-2 gap-3">
        {g.places.map((p) => (
          <div key={p.wiki} className="rounded-lg overflow-hidden border border-stoneLine break-inside-avoid">
            {p.image && <img src={imgSrc(p.image)} alt="" className="w-full h-32 object-cover" />}
            <div className="p-2.5">
              <div className="text-sm font-semibold text-ink">{pick(p.name, lang)}</div>
              <p className="text-xs text-muted-foreground mt-0.5">{pick(p.desc, lang)}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Cuisine({ g, code, lang, t }) {
  if (!g.cuisine) return null;
  return (
    <section>
      <H3>{t("guide_cuisine")}</H3>
      <div className="grid grid-cols-3 gap-3" data-testid={`ebook-cuisine-${code}`}>
        {["dish", "dessert", "street"].map((k) => g.cuisine[k] && (
          <div key={k} className="rounded-lg overflow-hidden border border-stoneLine break-inside-avoid">
            {g.cuisine[k].image && <img src={imgSrc(g.cuisine[k].image)} alt="" className="w-full h-24 object-cover" />}
            <div className="p-2.5">
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t(KIND_LABEL[k])}</div>
              <div className="text-sm font-semibold text-ink leading-tight">{pick(g.cuisine[k].name, lang)}</div>
              <p className="text-xs text-muted-foreground mt-1 leading-snug">{pick(g.cuisine[k].desc, lang)}</p>
              <p className="text-[10px] text-ink/70 mt-1.5">{t("guide_ingredients")}: {(g.cuisine[k].ingredients[lang] || g.cuisine[k].ingredients.en).join(", ")}</p>
              {g.cuisine[k].recipe && <Link to={`/recipe/${code}/${k}`} className="text-[11px] text-ochre hover:underline print:hidden">→ {t("recipe_view")}</Link>}
            </div>
          </div>
        ))}
      </div>
      <div className="text-xs text-ink/80 mt-2">{t("guide_dishes")}: {g.dishes.map((d) => pick(d, lang)).join(" · ")}</div>
    </section>
  );
}

function Travel({ g, lang, t }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-6 break-inside-avoid">
        <section><H3>{t("guide_best_time")}</H3><p className="text-sm text-ink">{pick(g.best_time, lang)}</p></section>
        <section><H3>{t("guide_getting_around")}</H3><p className="text-sm text-ink/85">{pick(g.getting_around, lang)}</p></section>
      </div>
      {g.itinerary?.length > 0 && (
        <section className="break-inside-avoid">
          <H3>{t("guide_itinerary")}</H3>
          <ol className="space-y-1.5">
            {g.itinerary.map((d, i) => (
              <li key={d.en} className="flex gap-3 text-sm">
                <span className="w-5 h-5 shrink-0 rounded-full bg-ochre text-white text-[10px] font-bold flex items-center justify-center print:bg-ochre">{i + 1}</span>
                <span className="text-ink">{pick(d, lang)}</span>
              </li>
            ))}
          </ol>
        </section>
      )}
      <section className="break-inside-avoid"><H3>{t("guide_tip")}</H3><p className="text-sm text-ink/85 border-s-2 border-oasis ps-3">{pick(g.tip, lang)}</p></section>
    </>
  );
}

export default function EbookChapter({ country, index, codeToName }) {
  const { lang, t } = useLang();
  const name = country.names[lang] || country.names.en;
  const g = country.travel_guide;
  const borders = country.borders.map((b) => codeToName[b] || b);
  return (
    <article id={`ebook-${country.code}`} className="break-before-page pt-2 mb-14 print:mb-0 scroll-mt-24 space-y-6" data-testid={`ebook-chapter-${country.code}`}>
      <header className="flex items-center gap-5 border-b-2 border-ochre pb-5">
        <img src={country.flag_url} alt="" className="w-28 h-[76px] object-cover rounded-md border border-stoneLine shadow-sm shrink-0" />
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-ochre font-semibold">{t("ebook_chapter")} {index}</div>
          <h2 className="text-3xl sm:text-4xl font-display font-bold text-ink leading-tight" data-testid={`ebook-chapter-title-${country.code}`}>{name}</h2>
          <div className="text-sm text-muted-foreground mt-1" dir="rtl">{country.names.ar}</div>
        </div>
      </header>

      <p className="text-base leading-relaxed text-ink/85">{pick(country.intro, lang)}</p>
      <Facts country={country} lang={lang} t={t} />

      <section className="break-inside-avoid"><H3>{t("economy")}</H3><p className="text-sm leading-relaxed text-ink/85">{pick(country.economy_base, lang)}</p></section>
      <section className="break-inside-avoid">
        <H3>{t("nature")}</H3>
        <ul className="space-y-1 text-sm">
          {["fauna", "mountains", "rivers"].map((k) => <li key={k}><span className="text-[10px] uppercase tracking-widest text-muted-foreground me-2">{t(k)}</span>{pick(country.nature[k], lang)}</li>)}
        </ul>
      </section>

      <div className="grid sm:grid-cols-[1fr_auto] gap-6">
        <HistoryBlock country={country} lang={lang} t={t} />
        <AgeBlock country={country} lang={lang} t={t} />
      </div>

      {g && <Places g={g} lang={lang} t={t} />}
      {g && <Cuisine g={g} code={country.code} lang={lang} t={t} />}
      {g && <Travel g={g} lang={lang} t={t} />}

      <section className="break-inside-avoid">
        <H3>{t("borders")}</H3>
        <p className="text-sm text-ink/85">{borders.length ? borders.join(" · ") : t("no_borders")}</p>
      </section>
    </article>
  );
}
